/**
 * notificationBatchService.ts
 *
 * Groups stale AuditDiff objects by user email into consolidated
 * UserNotificationBatch objects - ready for notification phase (Phase 4).
 *
 * No email sending here. Only grouping and deduplication logic.
 */

import { AuditDiff, UserNotificationBatch, PricingChange } from '../types';

/**
 * Takes an array of stale AuditDiff objects and a mapping of auditId → userEmail,
 * and produces one UserNotificationBatch per user.
 *
 * - Deduplicates changed tools across all audits for the same user
 * - Combines savings deltas
 * - Produces a single consolidated change summary per user
 */
export function groupAffectedAuditsByUser(
  staleDiffs: AuditDiff[],
  auditEmailMap: Record<string, string>,
): UserNotificationBatch[] {
  // Attach emails to diffs
  const diffsWithEmails = staleDiffs.map(diff => ({
    ...diff,
    userEmail: diff.userEmail ?? auditEmailMap[diff.auditId] ?? 'unknown',
  }));

  // Group diffs by email
  const grouped = new Map<string, AuditDiff[]>();
  for (const diff of diffsWithEmails) {
    const email = diff.userEmail ?? 'unknown';
    if (!grouped.has(email)) grouped.set(email, []);
    grouped.get(email)!.push(diff);
  }

  const batches: UserNotificationBatch[] = [];

  for (const [email, diffs] of grouped.entries()) {
    const combinedDeltaMonthly = diffs.reduce((sum, d) => sum + d.deltaMonthlySavings, 0);
    const combinedDeltaAnnual = diffs.reduce((sum, d) => sum + d.deltaAnnualSavings, 0);

    // Deduplicate changed tools across all audits for this user
    const seenToolPlanKeys = new Set<string>();
    const dedupedChangedTools: PricingChange[] = [];
    for (const diff of diffs) {
      for (const change of diff.changedTools) {
        const key = `${change.toolId}:${change.planId}:${change.changeType}`;
        if (!seenToolPlanKeys.has(key)) {
          seenToolPlanKeys.add(key);
          dedupedChangedTools.push(change);
        }
      }
    }

    const changeSummary = buildBatchSummary(email, diffs, combinedDeltaMonthly, dedupedChangedTools);

    batches.push({
      userEmail: email,
      affectedAudits: diffs,
      changedTools: dedupedChangedTools,
      combinedDeltaMonthlySavings: combinedDeltaMonthly,
      combinedDeltaAnnualSavings: combinedDeltaAnnual,
      changeSummary,
    });
  }

  return batches;
}

function buildBatchSummary(
  email: string,
  diffs: AuditDiff[],
  combinedDeltaMonthly: number,
  changedTools: PricingChange[],
): string {
  const lines: string[] = [];
  lines.push(`${diffs.length} audit(s) for ${email} are now stale.`);

  if (Math.abs(combinedDeltaMonthly) >= 1) {
    const sign = combinedDeltaMonthly >= 0 ? '+' : '';
    lines.push(
      `Combined savings potential shifted by ${sign}$${combinedDeltaMonthly.toFixed(2)}/month.`,
    );
  }

  if (changedTools.length > 0) {
    const toolNames = [...new Set(changedTools.map(c => c.toolName))].join(', ');
    lines.push(`Pricing changed on: ${toolNames}.`);
  }

  return lines.join(' ');
}
